import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { gameSlice } from './slices/game/game';
import { gameWindowSlice } from './slices/gameWindow/gameWindow';
import { geolocationSlice } from './slices/geolocation/geolocation';
import { customerDataSlice } from './slices/customerData/customerData';
import { recentlyPlayedSlice } from '@/store/slices/recentlyPlayed/recentlyPlayed';
import { queueSlice, QueueSliceProps } from '@/store/slices/queue/queue';
import { userContextSlice } from '@/store/slices/userContext/userContext';
import { runtimeSlice } from '@/store/slices/runtime/runtime';
import { GeolocationSlice } from './slices/geolocation/types';
import { CustomerDataSlice } from './slices/customerData/types';
import { GameSlice } from './slices/game/types';
import { RecentlyPlayedSlice } from './slices/recentlyPlayed/types';
import { UserContextSlice } from './slices/userContext/types';
import { ThemeSlice } from './slices/theme/types';
import { TimerSlice } from './slices/timer/types';
import { GameWindowSlice } from './slices/gameWindow/types';
import { timerSlice } from './slices/timer/timer';
import { themeSlice } from './slices/theme/theme';
import { SessionSlice } from './slices/session/types';
import { CommonUiSlice } from './slices/commonUi/types';
import { sessionSlice } from './slices/session/session';
import { commonUiSlice } from './slices/commonUi/commonUi';
import { RuntimeSlice } from './slices/runtime/types';
import { showoffSlice, ShowoffSlice } from './slices/showOff/showoff';
import { lockUISlice } from './slices/lockUI/lockUI';
import { LockUISlice } from './slices/lockUI/types';
import { swjSlice, SwjSliceProps } from './slices/swj/swj';
import {
  deviceSafeZoneSlice,
  DeviceSafeZoneSliceProps,
} from './slices/deviceSafeZone/deviceSafeZone';

export type RootState = GameSlice &
  SessionSlice &
  CommonUiSlice &
  GameWindowSlice &
  GeolocationSlice &
  TimerSlice &
  ThemeSlice &
  CustomerDataSlice &
  QueueSliceProps &
  RecentlyPlayedSlice &
  UserContextSlice &
  RuntimeSlice &
  ShowoffSlice &
  LockUISlice &
  SwjSliceProps &
  DeviceSafeZoneSliceProps;

export type SetState<T> = {
  set: (fn: (state: T) => object, replace?: false, actionName?: string) => void;
  get: () => RootState;
};

// ========== Unified Store ==========
export const useStore = create<RootState>()(
  devtools(
    (set, get) => ({
      ...gameSlice({ set, get }),
      ...sessionSlice({ set, get }),
      ...commonUiSlice({ set, get }),
      ...gameWindowSlice({ set, get }),
      ...geolocationSlice({ set, get }),
      ...timerSlice({ set, get }),
      ...themeSlice({ set, get }),
      ...customerDataSlice({ set, get }),
      ...queueSlice({ set, get }),
      ...recentlyPlayedSlice({ set, get }),
      ...userContextSlice({ set, get }),
      ...runtimeSlice(),
      ...showoffSlice({ set, get }),
      ...lockUISlice({ set, get }),
      ...swjSlice({ set, get }),
      ...deviceSafeZoneSlice({ set, get }),
    }),
    {
      name: 'store',
      predicate: (_state, action) =>
        !(typeof action?.type === 'string' && action.type.startsWith('timer/')),
    },
  ),
);
